<script setup lang="ts">
import { AUTH_QUERY_KEYS } from '~/queries/auth/token'

const nuxtApp = useNuxtApp()
const toast = useToast()

// Safely access $pwa - it may be undefined on initial load or if PWA module isn't loaded
const $pwa = computed(() => nuxtApp.$pwa)
const { $groundcover, $pendo } = useNuxtApp()

const { user, loggedIn } = useUserSession()
const { clearCache } = usePdfCache()
const chatAvailable = useChatAvailable()
const { ping, connectionState } = useUserAgent()

/* Shell layouts render the agent bar beside their sidebar; full-screen pages
 * (e.g. `layout: false`) get the same bounded column here. Pages size to
 * that column with `cqh` units, never the viewport. */
const route = useRoute()
const SHELL_LAYOUTS = new Set(['default', 'settings'])
const pinnedAgentBar = computed(
  () =>
    loggedIn.value &&
    route.meta.layout !== 'auth' &&
    !SHELL_LAYOUTS.has(String(route.meta.layout ?? 'default')),
)

/* Clear PDF blob storage on app refresh */
onMounted(() => {
  clearCache()
})

watch(
  loggedIn,
  (val) => {
    if (!val) {
      clearCache()
      const queryCache = useQueryCache()
      queryCache.invalidateQueries({ key: AUTH_QUERY_KEYS.token })
      $groundcover.updateConfig({ user: null })
    }
  },
  { immediate: true },
)

// Ping the UserAgent once both logged in and WebSocket is connected
watchEffect(() => {
  if (loggedIn.value && connectionState.value === 'connected') {
    ping()
  }
})

whenever(
  () => $pwa.value && 'needRefresh' in $pwa.value && $pwa.value.needRefresh,
  () => {
    const pwa = $pwa.value
    if (!pwa || !pwa.updateServiceWorker) return

    toast.add({
      title: 'New version available',
      description: 'Please refresh to use the new version',
      color: 'primary',
      progress: false,
      close: false,
      duration: 0,
      actions: [
        {
          label: 'Reload',
          variant: 'subtle',
          icon: 'i-ph-arrow-clockwise',
          onClick: () => {
            pwa.updateServiceWorker?.()
          },
        },
      ],
    })
  },
  {
    immediate: true,
  },
)

watch(
  [user],

  ([currentUser]) => {
    if (!currentUser) {
      $groundcover.updateConfig({ user: null })
      return
    }

    const { email, name, sub, org_id } = currentUser
    const roles = currentUser['https://auth.rialtic.io/roles']

    $groundcover.identifyUser({
      id: sub,
      name,
      email,
      role: roles?.join(', ') ?? '',
      organization: email.split('@')[1],
      properties: {
        org_id,
      },
    })

    if (!$pendo) return

    // @ts-expect-error - nuxt-pendo types seem to be outdated, this is working as expected
    $pendo.identify({
      visitor: {
        id: email,
        email,
        full_name: name,
        role: roles[0] ?? '',
        roles: roles?.join(', ') ?? '',
      },
      account: {
        id: email.split('@')[1],
        rialtic_app: 'admin',
      },
    })
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <UApp :toaster="{ expand: false }">
    <NuxtLoadingIndicator
      color="repeating-linear-gradient(to right,#6F7CCD 0%,#28B7F0 62%,#35CDDA 100%)"
    />
    <NuxtPwaManifest />
    <NuxtRouteAnnouncer />
    <KeyboardShortcutsModal />
    <div :class="pinnedAgentBar && 'flex h-dvh flex-col'">
      <div
        :class="
          pinnedAgentBar && '@container-size min-h-0 flex-1 overflow-auto'
        "
      >
        <NuxtLayout>
          <NuxtPage />
        </NuxtLayout>
      </div>
      <AgentBar v-if="pinnedAgentBar && chatAvailable" />
    </div>
    <AgentChatPanel v-if="loggedIn && chatAvailable" />
  </UApp>
</template>
